// components/maintenance/MaintenanceDialog.tsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Users, Clock, Settings, X, StopCircle, CheckCircle } from 'lucide-react';
import { supabaseService } from '../../SupabaseService';

interface MaintenanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMaintenanceStart: () => void;
  isMaintenanceActive?: boolean;
  maintenanceStatus?: any;
}

const MaintenanceDialog: React.FC<MaintenanceDialogProps> = ({
  isOpen,
  onClose,
  onMaintenanceStart,
  isMaintenanceActive = false,
  maintenanceStatus = null
}) => {
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [maintenanceMessage, setMaintenanceMessage] = useState('Systemoppdatering pågår. Vi er tilbake snart!');
  const [estimatedMinutes, setEstimatedMinutes] = useState(30);
  const [countdown, setCountdown] = useState(300); // 5 min
  const [countdownActive, setCountdownActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Hvis vedlikehold er aktivt, vis info om det
    if (isMaintenanceActive && maintenanceStatus) {
      setMaintenanceMessage(maintenanceStatus.message || 'Vedlikehold pågår');
    }
  }, [isMaintenanceActive, maintenanceStatus]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCountdownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdownActive, countdown]);

  const checkActiveUsers = async () => {
    setIsLoading(true);
    try {
      const users = await supabaseService.getActiveUsers();
      setActiveUsers(users);
    } catch (error) {
      console.error('Feil ved henting av aktive brukere:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const startCountdown = async () => {
    await supabaseService.sendMaintenanceWarning(maintenanceMessage, 5);
    setCountdownActive(true);
    setCountdown(300); // Reset to 5 minutes
  };

  const startMaintenance = async () => {
    setIsLoading(true);
    try {
      const success = await supabaseService.setMaintenanceMode(true, maintenanceMessage, estimatedMinutes);
      if (success) {
        onMaintenanceStart();
        alert('Vedlikeholdsmodus aktivert!');
        window.location.reload(); // Refresh for å oppdatere status
      } else {
        alert('Kunne ikke aktivere vedlikeholdsmodus. Prøv igjen.');
      }
    } catch (error) {
      console.error('Feil ved start av vedlikehold:', error);
      alert('Feil ved start av vedlikehold.');
    } finally {
      setIsLoading(false);
    }
  };

  const endMaintenance = async () => {
    if (!confirm('Er du sikker på at du vil avslutte vedlikeholdsmodus?')) {
      return;
    }

    setIsLoading(true);
    try {
      const success = await supabaseService.setMaintenanceMode(false);
      if (success) {
        alert('Vedlikeholdsmodus avsluttet!');
        window.location.reload(); // Refresh for å oppdatere hele appen
      } else {
        alert('Kunne ikke avslutte vedlikeholdsmodus. Prøv igjen.');
      }
    } catch (error) {
      console.error('Feil ved avslutning av vedlikehold:', error);
      alert('Feil ved avslutning av vedlikehold.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-orange-50 border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="text-orange-600" size={24} />
            <h3 className="text-xl font-semibold text-gray-900">
              {isMaintenanceActive ? 'Vedlikeholdsmodus er aktiv' : 'Planlegg vedlikehold'}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Vis status hvis vedlikehold er aktivt */}
          {isMaintenanceActive && maintenanceStatus && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-red-600 mt-0.5" size={20} />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-2">Vedlikehold pågår</h4>
                  <div className="space-y-1 text-sm text-red-800">
                    <p><strong>Melding:</strong> {maintenanceStatus.message}</p>
                    {maintenanceStatus.start_time && (
                      <p><strong>Startet:</strong> {new Date(maintenanceStatus.start_time).toLocaleString('nb-NO')}</p>
                    )}
                    {maintenanceStatus.estimated_end && (
                      <p><strong>Estimert ferdig:</strong> {new Date(maintenanceStatus.estimated_end).toLocaleString('nb-NO')}</p>
                    )}
                    {maintenanceStatus.set_by && (
                      <p><strong>Startet av:</strong> {maintenanceStatus.set_by}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hvis vedlikehold IKKE er aktivt, vis planleggingsgrensesnitt */}
          {!isMaintenanceActive && (
            <>
              {/* Aktive brukere */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <Users className="mr-2" size={18} />
                    Aktive brukere ({activeUsers.length})
                  </h4>
                  <button
                    onClick={checkActiveUsers}
                    disabled={isLoading}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isLoading ? 'Sjekker...' : 'Oppdater'}
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                  {activeUsers.length === 0 ? (
                    <p className="text-gray-500 text-sm">Ingen aktive brukere funnet (siste 15 min)</p>
                  ) : (
                    <div className="space-y-2">
                      {activeUsers.map((user, index) => (
                        <div key={index} className="flex justify-between items-center text-sm">
                          <span className="font-medium">{user.user}</span>
                          <span className="text-gray-500">
                            {user.minutesAgo === 0 ? 'Akkurat nå' : `${user.minutesAgo} min siden`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Vedlikeholdsmelding */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Melding til brukere:
                </label>
                <textarea
                  value={maintenanceMessage}
                  onChange={(e) => setMaintenanceMessage(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  rows={3}
                  placeholder="Beskriv hva som skjer under vedlikeholdet..."
                />
              </div>

              {/* Estimert varighet */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estimert varighet:
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="1"
                    max="480"
                    value={estimatedMinutes}
                    onChange={(e) => setEstimatedMinutes(parseInt(e.target.value) || 30)}
                    className="w-20 border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-600">minutter</span>
                </div>
              </div>

              {/* Countdown */}
              {countdownActive && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <AlertTriangle className="text-yellow-600" size={20} />
                    <div className="flex-1">
                      <div className="font-semibold text-yellow-800">Varsel sendt til alle brukere</div>
                      <div className="text-sm text-yellow-700">Vedlikehold starter automatisk om:</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-yellow-800 flex items-center">
                        <Clock className="mr-2" size={20} />
                        {formatTime(countdown)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for aktive brukere */}
              {activeUsers.length > 0 && !countdownActive && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="text-red-600" size={18} />
                    <span className="text-sm font-medium text-red-800">
                      {activeUsers.length} bruker{activeUsers.length !== 1 ? 'e' : ''} er fortsatt aktiv{activeUsers.length !== 1 ? 'e' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    Vurder å sende varsel først for å gi brukerne tid til å lagre arbeidet sitt.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Lukk
          </button>
          
          <div className="flex space-x-3">
            {isMaintenanceActive ? (
              // Hvis vedlikehold er aktivt, vis avslutt-knapp
              <button
                onClick={endMaintenance}
                disabled={isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                <CheckCircle size={18} />
                <span>Avslutt vedlikehold</span>
              </button>
            ) : (
              // Hvis vedlikehold IKKE er aktivt, vis start-knapper
              <>
                {!countdownActive ? (
                  <>
                    <button
                      onClick={startCountdown}
                      disabled={isLoading}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors"
                    >
                      Send 5-min varsel
                    </button>
                    <button
                      onClick={startMaintenance}
                      disabled={isLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Start vedlikehold nå
                    </button>
                  </>
                ) : (
                  <button
                    onClick={startMaintenance}
                    disabled={isLoading || countdown > 0}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {countdown > 0 ? `Start om ${formatTime(countdown)}` : 'Start vedlikehold nå'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceDialog;